import React from "react";
import { styled, StyleException } from "../../theme/styled";
import { Illustration } from "../illustration/Illustration";
import { Body, Caption, Heading } from "../typography/Typography";
import { Margin } from "../util/Margin";

interface BaseProps {
  copy?: {
    error_generic__title_alternative_2: string;
    error__generic_error_alternative_2: string;
  };
  dataTestId?: string;
  exceptionId?: string;
  market?: "fi" | "se";
  body?: string;
  title?: string;
  children?: React.ReactNode;
  useDefaultTexts?: boolean;
  illustration?:
    | "GenericError"
    | "GenericErrorConnection"
    | "GenericErrorFlower"
    | "GenericErrorIceCream"
    | "GenericErrorTechnical"
    | "GenericErrorVase";
}

type ErrorPageProps =
  // Default error page.
  | (BaseProps & { useDefaultTexts: true })
  // Custom error page with at least a string (could provide more as well).
  | (BaseProps & { title: string })
  // Custom error page with at least children.
  | (BaseProps & { children: React.ReactNode });

export const ErrorPage = (props: ErrorPageProps): React.JSX.Element => {
  const { body: defaultBody, title: defaultTitle } = useSafeCopy(
    props.market,
    props.copy,
  );

  const title = props.useDefaultTexts ? defaultTitle : props.title;
  const body = props.useDefaultTexts ? defaultBody : props.body;
  const illustrationType = props.illustration ?? "GenericError";
  const IllustrationComponent = Illustration[illustrationType];

  const ExceptionId = (): React.JSX.Element | null =>
    props.exceptionId
      ? (
        <Margin top={32}>
          <Caption color="$text-secondary">
            Error code: {props.exceptionId}
          </Caption>
        </Margin>
      )
      : null;

  const Children = (): React.JSX.Element | null =>
    props.children ? <Margin top={32}>{props.children}</Margin> : null;

  return (
    <ErrorWrapper
      data-component-type="ErrorPage"
      data-test-id={props.dataTestId}
    >
      <IllustrationComponent />
      <Margin top={32} />
      {title && <Heading size="medium" as="h1">{title}</Heading>}
      {body && <Body size="medium">{body}</Body>}
      <Children />
      <ExceptionId />
    </ErrorWrapper>
  );
};

const ErrorWrapper = styled("div")({
  margin: StyleException("120px auto 0 auto"),
  padding: ["$spacing-32", "$spacing-24"],
  maxWidth: "400px",
  textAlign: "center",
});

const useSafeCopy = (
  market: ErrorPageProps["market"] = "se",
  copy: ErrorPageProps["copy"],
): { title: string; body: string } => {
  if (
    copy?.error_generic__title_alternative_2 &&
    copy.error__generic_error_alternative_2
  ) {
    return {
      title: copy.error_generic__title_alternative_2,
      body: copy.error__generic_error_alternative_2,
    };
  }
  const supportEmail = market === "se" ? "support@kivra.com" : "tuki@kivra.fi";
  const browserLanguage = window.navigator.language
    .substring(0, 2)
    .toLowerCase();

  switch (browserLanguage) {
    case "sv":
      return {
        title: "Något gick fel när applikationen skulle startas",
        body: `Om felet kvarstår kontakta ${supportEmail}`,
      };
    case "fi":
      return {
        title: "Jokin meni vikaan sovelluksen käynnistyksessä",
        body: `Ongelman jatkuessa olethan yhteydessä ${supportEmail}`,
      };
    default:
      return {
        title: "Something went wrong when initializing the application",
        body: `Please contact ${supportEmail} if the problem persists`,
      };
  }
};
